// Окно «Назначить ведущего П-N» (038: FR-001 – FR-019; contracts/ui.md 038; research Р4 – Р8, Р11;
// LEAD-01 – LEAD-08). Эталон — gr8r-studio, Members → «Invite member»: заголовок с подзаголовком,
// поля, плашка о роли, внизу «Отмена» и одна акцентная кнопка справа.
//
// Шаг поиска: подзаголовок — что такое ведущий; строка «Сейчас ведёт: <ФИО>» или «Ведущего нет»;
// одно поле «ФИО или почта» (от двух знаков, minutes.lead.lookup); разделы «Пользователи»
// и «Из справочника — нет учётной записи» (заголовок раздела — подпись списка, пустой раздел
// скрыт) и внизу «Создать пользователя «…»» (введена почта — «Создать пользователя <почта>»).
// Строки — кнопки одного списка: ↑ ↓ Home End по всем разделам, из поля ↓ — на первую, Enter —
// выбор. Недоступная строка — aria-disabled с причиной второй строкой (внешний пользователь) или
// выноской «Создать пользователя может администратор клиента: …» (нет users.create, FR-017).
// Длинные ФИО, почта и организация — в одну строку с многоточием, полностью — в подсказке.
// «Поиск…» — только если ответ дольше 0,3 с; «Никого не нашлось» и число найденных — в живой
// области; сбой — причина, код, номер операции и «Повторить». Подвал — только «Отмена»: выбор
// строки и есть действие.
//
// Назначение (FR-008 – FR-010, FR-019): окно сообщает серверу, кого считает ведущим
// (expectedLeadId). Ведущего нет — сразу; есть — окно подтверждения с последствием для прежнего
// (leadAfterAccessText). После — тост «Ведущий П-N: <ФИО>» с «Отменить» (undo: true). «Ведущего
// уже сменили» (MIN-025) — окно остаётся, над поиском выноска, строка «Сейчас ведёт» перечитана.
// Протокол ушёл в корзину или из режима ведения (REC-002, MIN-001) — тост с причиной, окно
// закрывается. Другой сбой — тост с кодом и «Повторить», окно остаётся.
//
// Шаг создания (FR-011 – FR-015, FR-018): в том же окне, вместо результатов. ФИО и почта —
// из справочника, из введённого текста или из почты; роль — «Ведущий П-N» без выбора; связь
// со справочником; прежний ведущий и последствие — вместо отдельного подтверждения. Почта вне
// корпоративных доменов — ошибка под полем и недоступная «Создать и назначить»; домен разрешён
// внешним доступом — путь «Открыть «Доступ»». REC-003 — «Назначить <ФИО>» (с подтверждением, если
// ведущий есть) или «в архиве»: признака архива в ответе нет — ФИО и архив окно читает из nextStep
// по шаблону каталога (existingOf). MIN-026 и REC-007 по personId (человека справочника нет) —
// выноска, окно остаётся. Подвал: «Назад» → «Отмена» → «Создать и назначить». Esc и «Назад» —
// к поиску с прежним текстом. MIN-025 несёт нынешнего ведущего в поле current — строка «Сейчас
// ведёт» обновляется сразу, последствие для него — перечитыванием minutes.lead.get.
//
// Волна ревью (T027, T029, T031, T032). Пометка «Ведёт сейчас» — у того, кого окно считает
// ведущим (строка «Сейчас ведёт»), а после MIN-025 поиск перечитывается: строка и список всегда
// об одном ведущем. Человек справочника связан, пока почта в поле — его почта (без регистра):
// другая почта — personId не уходит, связь «по почте»; вернули его почту — снова связан. Пока
// идёт «Создать и назначить», «Назад», Esc, ✕ и «Отмена» ждут ответа. «Повторить» в тосте
// помнит свой протокол и шаг: окно закрыто, показывает другой протокол или другое заполнение
// шага создания — отказ приходит тостом, чужое окно не закрывается. REC-002 и MIN-001 на поиск,
// minutes.lead.get и «Отменить» — тост с причиной, окно закрывается (без «Повторить»).
import {
  EmailInputSchema,
  LOOKUP_QUERY_MAX,
  ProtocolLeadSchema,
  emailStanding,
  renderError,
} from '@kp/contracts';
import type {
  CreateLeadBody,
  EmailPolicy,
  LeadAfterAccess,
  LeadState,
  MeResponse,
  ProtocolLead,
  SetLeadResponse,
} from '@kp/contracts';
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  Icon,
  Modal,
  ModalFooter,
  SearchField,
  TextField,
  Tooltip,
  useDelayedFlag,
  useToast,
} from '@kp/ui';
import type { LucideIcon } from '@kp/ui';
import { ArrowLeft, Contact, Link, UserPlus, UserRound, UserRoundCog } from '@kp/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { errorView, useErrorToast } from '../../app/errors.ts';
import type { ErrorView } from '../../app/errors.ts';
import { can } from '../../shell/permissions.ts';
import { useMe } from '../../shell/session.ts';
import { canChangeOwner } from './can.ts';
import { ListLoadError } from '../load-error.tsx';
import { emailError, fullNameError } from '../users/fields.ts';
import { leadStateKey, refreshLead, useLeadLookup, useLeadState } from './api.ts';
import {
  ARCHIVED_TEXT,
  BACK,
  CHANGE_LEAD_CONFIRM,
  CREATE_AND_ASSIGN,
  CREATE_SUBTITLE,
  EXISTING_HINT,
  EXTERNAL_ACCESS_HINT,
  EXTERNAL_USER_REASON,
  LEADS_NOW,
  LEAD_MEANING,
  LEAD_STATE_FAILED,
  LINK_BY_EMAIL,
  LOOKUP_FAILED,
  NO_LEAD,
  OPEN_ACCESS,
  OUTSIDE_DOMAINS,
  PEOPLE_HEADING,
  PERSON_GONE,
  SEARCHING,
  SEARCH_LABEL,
  SEARCH_PLACEHOLDER,
  TYPE_MORE,
  USERS_HEADING,
  adminsOnlyText,
  assignExistingLabel,
  assignLeadTitle,
  assignedText,
  changeLeadText,
  changeLeadTitle,
  createTitle,
  createUserText,
  createdText,
  currentLeadText,
  domainOnlyText,
  existingText,
  foundText,
  leadNowText,
  linkPersonText,
  nobodyText,
  roleText,
  undoneText,
} from './text.ts';
import './lead.css';

/** Протокол окна: номер — в заголовках, название — в плашке роли, ведущий — до ответа сервера. */
export interface LeadProtocol {
  id: string;
  number: number;
  title: string;
  /** Ведущий, которого знает место вызова (строка «Документов», окно «Доступ», экран протокола). */
  lead: ProtocolLead | null;
}

export interface AssignLeadDialogProps {
  protocol: LeadProtocol;
  open: boolean;
  onClose: () => void;
  /** Куда вернуть фокус после закрытия, если окно открыли из меню; иначе — откуда открыли. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * «Открыть «Доступ»» в шаге создания, если почта на домене внешнего доступа: окно закрывается,
   * страница открывает «Доступ». Нет — выноска без кнопки (окно открыто из самого «Доступа»).
   */
  onOpenAccess?: (() => void) | undefined;
}

/** Кого назначить: пользователь из поиска или владелец занятой почты (REC-003). */
interface Candidate {
  id: string;
  name: string;
}

interface Confirm {
  next: Candidate;
  current: ProtocolLead;
  after: LeadAfterAccess | null;
}

/** Чем заполнить шаг создания: человек справочника, введённый текст или почта. */
interface Seed {
  fullName: string;
  email: string;
  /** Человек справочника и его основная почта: связь — пока почта в поле та же (S2). */
  person: { id: string; name: string; email: string } | null;
}

type FieldName = 'fullName' | 'email';
type FieldErrors = Partial<Record<FieldName, string | undefined>>;

/** Почта уже у пользователя (REC-003): ФИО, архив, код и номер операции. */
interface Existing {
  id: string;
  name: string;
  archived: boolean;
  view: ErrorView;
}

const NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/**
 * ↑ ↓ Home End — по строкам всех разделов (ux.md, раздел 5); из поля ↓ — на первую строку,
 * остальные клавиши в поле двигают курсор. Недоступные строки (aria-disabled) — тоже в обходе:
 * причину слышно в программе чтения.
 */
function moveFocus(event: KeyboardEvent<HTMLElement>, list: HTMLElement | null): void {
  if (!NAV_KEYS.has(event.key)) return;
  const options = Array.from(list?.querySelectorAll<HTMLButtonElement>('[data-option]') ?? []);
  if (options.length === 0) return;
  if (event.target instanceof HTMLInputElement) {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    options[0]?.focus();
    return;
  }
  const index = options.indexOf(document.activeElement as HTMLButtonElement);
  if (index < 0) return;
  const target =
    event.key === 'ArrowDown'
      ? options[Math.min(index + 1, options.length - 1)]
      : event.key === 'ArrowUp'
        ? options[Math.max(index - 1, 0)]
        : event.key === 'Home'
          ? options[0]
          : options.at(-1);
  event.preventDefault();
  target?.focus();
}

/** «Причина. Следующий шаг» — без второй точки после ФИО «Рябов Р. Р.». */
function sentence(view: ErrorView): string {
  return `${view.message}${view.message.endsWith('.') ? '' : '.'} ${view.nextStep}`;
}

const MARK = '\u0000';

/**
 * REC-003: ФИО и архив — из текста ответа по шаблону каталога (параметры fullName и archived):
 * отдельных полей у ответа нет. Шаблон не совпал — null, окно показывает текст ответа.
 */
export function existingOf(nextStep: string): { name: string; archived: boolean } | null {
  for (const archived of [true, false]) {
    const [before = '', after = ''] = renderError('REC-003', {
      fullName: MARK,
      archived,
    }).nextStep.split(MARK);
    if (
      nextStep.length > before.length + after.length &&
      nextStep.startsWith(before) &&
      nextStep.endsWith(after)
    ) {
      return { name: nextStep.slice(before.length, nextStep.length - after.length), archived };
    }
  }
  return null;
}

/** Политика почты сеанса (022, research Р2): нет настройки внешнего доступа — как «Выключен». */
function sessionPolicy(me: Pick<MeResponse, 'domains' | 'externalAccess'>): EmailPolicy {
  return {
    corporate: me.domains ?? [],
    externalAccess: me.externalAccess?.mode ?? 'off',
    externalDomains: me.externalAccess?.domains ?? [],
  };
}

function isEmail(value: string): boolean {
  return EmailInputSchema.safeParse(value).success;
}

/** Та же почта: сервер сравнивает почты без регистра и пробелов по краям (normalizeEmail). */
function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Отказы, которые окно показывает выноской или под полем; «Повторить» им не поможет. Окно уже
 * закрыто или показывает другое (повтор из тоста) — они приходят тостом без «Повторить».
 */
const REFUSALS = [
  'MIN-025',
  'MIN-026',
  'REC-003',
  'REC-004',
  'REC-007',
  'ACC-007',
  'REC-002',
  'MIN-001',
] as const;

/** Текст строки сокращён многоточием: подсказка с полным текстом. */
function useClipped(ref: RefObject<HTMLElement | null>, text: string): boolean {
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () =>
      setClipped(
        Array.from(element.querySelectorAll<HTMLElement>('.kp-ald-clip')).some(
          (part) => part.scrollWidth > part.clientWidth + 1,
        ),
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, text]);
  return clipped;
}

function Option({
  icon,
  name,
  sub,
  note,
  badge,
  disabled = false,
  describedBy,
  onChoose,
}: {
  icon: LucideIcon;
  name: string;
  /** Вторая строка: почта, организация. */
  sub?: string | undefined;
  /** Причина недоступности — строкой под ФИО и почтой. */
  note?: string | undefined;
  badge?: ReactNode;
  disabled?: boolean;
  describedBy?: string | undefined;
  onChoose: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const full = [name, sub].filter(Boolean).join(' · ');
  const clipped = useClipped(ref, full);
  return (
    <li>
      <Tooltip content={full} disabled={!clipped} side="top">
        <button
          ref={ref}
          type="button"
          data-option=""
          className="kp-lead-option"
          aria-disabled={disabled || undefined}
          aria-describedby={describedBy}
          onClick={() => {
            if (!disabled) onChoose();
          }}
        >
          <Icon icon={icon} className="t-ico" />
          <span className="kp-ald-text">
            <span className="kp-ald-clip">{name}</span>
            {sub ? <span className="kp-ald-sub kp-ald-clip">{sub}</span> : null}
            {note ? <span className="kp-ald-sub">{note}</span> : null}
          </span>
          {badge}
        </button>
      </Tooltip>
    </li>
  );
}

/** Раздел результатов: заголовок — подпись списка. */
function Section({ heading, children }: { heading: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="kp-ald-sec">
      <h3 id={id} className="kp-ald-h">
        {heading}
      </h3>
      <ul className="kp-ald-list" aria-labelledby={id}>
        {children}
      </ul>
    </section>
  );
}

export function AssignLeadDialog({
  protocol,
  open,
  onClose,
  returnFocusRef,
  onOpenAccess,
}: AssignLeadDialogProps) {
  const { id, number, title } = protocol;
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const showError = useErrorToast();
  const canCreateUser = can(me.permissions, ['users.create']);
  const domains = me.domains ?? [];

  const [text, setText] = useState('');
  const [step, setStep] = useState<'search' | 'create'>('search');
  /** «Ведущего уже сменили» (MIN-025): выноска над поиском или над полями. */
  const [stale, setStale] = useState<ErrorView | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Шаг создания.
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [person, setPerson] = useState<Seed['person']>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [existing, setExisting] = useState<Existing | null>(null);
  /** Отказ, не попавший в поле (MIN-026, человек справочника не найден): выноска над полями. */
  const [refusal, setRefusal] = useState<ErrorView | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const adminsId = useId();
  const formId = useId();
  const emailId = `lead-email${useId()}`;

  const lead = useLeadState(id, open);
  const lookup = useLeadLookup(id, text, open && step === 'search');
  const searching = useDelayedFlag(lookup.loading);
  const current = lead.data ? lead.data.lead : protocol.lead;
  const after = lead.data?.after ?? null;

  // Окно закрыли: следующее открытие — с пустого поиска, без подтверждения и отказов шага
  // создания (повтор из тоста мог прийти, когда окно уже закрыто, W3).
  if (
    !open &&
    (text !== '' ||
      step !== 'search' ||
      stale !== null ||
      confirmOpen ||
      existing !== null ||
      refusal !== null ||
      pending)
  ) {
    setText('');
    setStep('search');
    setStale(null);
    setConfirmOpen(false);
    setExisting(null);
    setRefusal(null);
    setPending(false);
  }

  /**
   * Что окно показывает сейчас (W3, W6): протокол (null — закрыто или снято со страницы) и номер
   * заполнения шага создания — растёт при открытии, смене протокола, переходе в шаг и «Назад».
   * Ответ на повтор из тоста применяется к окну, только если они те же, что при запросе.
   */
  const shownRef = useRef<string | null>(null);
  const formRef = useRef(0);
  useEffect(() => {
    shownRef.current = open ? id : null;
    formRef.current += 1;
    return () => {
      shownRef.current = null;
    };
  }, [open, id]);

  // Смена шага — фокус в поле поиска или в первое пустое поле шага создания.
  const focusNext = useRef<RefObject<HTMLInputElement | null> | null>(null);
  useEffect(() => {
    focusNext.current?.current?.focus();
    focusNext.current = null;
  }, [step]);

  /** Кого окно считает ведущим: ответ minutes.lead.get; сбой — ведущий места вызова. */
  const leadNow = async (): Promise<LeadState> => {
    if (lead.data && !lead.isFetching) return lead.data;
    const result = await lead.refetch();
    return result.data ?? lead.data ?? { lead: protocol.lead, after: null };
  };

  /**
   * MIN-025: нынешний ведущий — в поле current ответа ({ userId, name } или null — ведущего сняли):
   * строка «Сейчас ведёт» сразу о нём; что останется у него после смены — перечитыванием.
   */
  const staleRefused = (error: unknown) => {
    setStale(errorView(error));
    const now = isApiError(error)
      ? ProtocolLeadSchema.nullable().safeParse(error.body.current)
      : null;
    if (now?.success) {
      queryClient.setQueryData<LeadState>(leadStateKey(id), { lead: now.data, after: null });
    }
    void lead.refetch();
    // Пометка «Ведёт сейчас» считается по строке «Сейчас ведёт», но и список — заново (W1).
    void queryClient.invalidateQueries({
      queryKey: ['api', 'minutes.lead.lookup', { id }],
    });
  };

  /** Протокол ушёл в корзину или из режима ведения: окно закрывается. */
  const gone = (error: unknown) => isApiError(error, 'REC-002') || isApiError(error, 'MIN-001');

  /** Окно открыто для этого протокола (а не закрыто и не показывает другой). */
  const here = () => shownRef.current === id;

  /**
   * Окно уже закрыто или показывает другое (повтор из тоста, W3): отказ — тостом без «Повторить»,
   * другой сбой — тостом с «Повторить». REC-007 по полю — текст поля: полей на экране нет.
   */
  const refuseAway = (error: unknown, retry: () => void) => {
    if (isApiError(error, 'MIN-025') || gone(error)) refreshLead(queryClient, id);
    if (!REFUSALS.some((code) => isApiError(error, code))) {
      showError(error, retry);
      return;
    }
    const field = isApiError(error, 'REC-007') ? Object.values(error.fields)[0] : undefined;
    const view = field
      ? { ...errorView(error), message: field, nextStep: PERSON_GONE }
      : errorView(error);
    // Без второй точки после ФИО «Рябов Р. Р.»: тост сам ставит точку перед «Код ошибки».
    toast.error(sentence(view).replace(/\.$/, ''), {
      code: view.code,
      operation: view.operation,
      duration: 'long',
    });
  };

  /** «Отменить» в тосте: окно уже закрыто — только запрос, тост итога и перечитывание. */
  const undo = (result: SetLeadResponse) => {
    const back = async (): Promise<void> => {
      try {
        const done = await api('minutes.lead.set', {
          params: { id },
          body: {
            userId: result.previous?.userId ?? null,
            expectedLeadId: result.lead?.userId ?? null,
            undo: true,
          },
        });
        toast.ok(undoneText(number, done.lead?.name ?? null));
      } catch (error) {
        if (gone(error)) {
          // Протокол ушёл: «Повторить» не поможет; окно этого протокола закрывается (W5).
          if (here()) onClose();
          showError(error);
        } else {
          showError(error, () => void back());
        }
      } finally {
        refreshLead(queryClient, id);
      }
    };
    void back();
  };

  /**
   * Назначить. MIN-025 — выноска, окно остаётся; REC-002, MIN-001 — тост, окно закрывается; другой
   * сбой — тост с «Повторить» и ошибка дальше: окно подтверждения остаётся открытым. Повтор из
   * тоста, когда окно закрыто или показывает другой протокол, окно не трогает (W3); удачный повтор
   * закрывает и подтверждение.
   */
  const assign = async (candidate: Candidate, expected: string | null): Promise<void> => {
    if (here()) setStale(null);
    try {
      const result = await api('minutes.lead.set', {
        params: { id },
        body: { userId: candidate.id, expectedLeadId: expected },
      });
      refreshLead(queryClient, id);
      if (here()) {
        setConfirmOpen(false);
        onClose();
      }
      // 040 (сквозные H2): «Отменить» — только тому, кто после смены сохраняет право сменить
      // владельца (администратору или новому владельцу); прежний владелец его теряет — отмена
      // получила бы ACC-001.
      const canUndo = result.changed && canChangeOwner(me, result.lead);
      toast.ok(
        assignedText(number, result.lead?.name ?? candidate.name),
        canUndo ? { action: { label: 'Отменить', onClick: () => undo(result) } } : undefined,
      );
    } catch (error) {
      const retry = () => void assign(candidate, expected).catch(() => undefined);
      if (!here()) {
        refuseAway(error, retry);
        return;
      }
      if (isApiError(error, 'MIN-025')) {
        staleRefused(error);
        return;
      }
      if (gone(error)) {
        refreshLead(queryClient, id);
        onClose();
        showError(error);
        return;
      }
      showError(error, retry);
      throw error;
    }
  };

  /** Выбор пользователя: ведущего нет — сразу, есть — подтверждение; нынешний — ничего. */
  const choose = async (candidate: Candidate): Promise<void> => {
    const state = await leadNow();
    if (state.lead?.userId === candidate.id) return;
    if (state.lead) {
      setConfirm({ next: candidate, current: state.lead, after: state.after });
      setConfirmOpen(true);
      return;
    }
    await assign(candidate, null).catch(() => undefined);
  };

  const startCreate = (seed: Seed) => {
    setFullName(seed.fullName);
    setEmail(seed.email);
    setPerson(seed.person);
    setErrors({});
    setSubmitted(false);
    setExisting(null);
    setRefusal(null);
    setStale(null);
    formRef.current += 1;
    focusNext.current = seed.fullName !== '' && seed.email === '' ? emailRef : nameRef;
    setStep('create');
  };

  const back = () => {
    setStale(null);
    formRef.current += 1;
    focusNext.current = searchRef;
    setStep('search');
  };

  // --- Шаг создания ---

  const trimmedEmail = email.trim();
  const policy = sessionPolicy(me);
  const standing =
    domains.length > 0 && isEmail(trimmedEmail) ? emailStanding(policy, trimmedEmail) : 'corporate';
  const outside = standing !== 'corporate';
  /** Человек справочника — пока почта в поле его (S2); другая почта — связь «по почте». */
  const linked = person && sameEmail(person.email, trimmedEmail) ? person : null;

  const validate = (values: { fullName: string; email: string }): FieldErrors => ({
    fullName: fullNameError(values.fullName),
    // Домен — живой проверкой ниже (outside); здесь только формат с примером.
    email: isEmail(values.email.trim()) ? undefined : emailError(values.email, domains),
  });

  const recheck = (changed: Partial<Record<FieldName, string>>, field: FieldName) => {
    if (!submitted) return;
    const found = validate({ fullName, email, ...changed });
    setErrors((now) => ({ ...now, [field]: found[field] }));
  };

  const showErrors = (found: FieldErrors) => {
    setErrors(found);
    if (found.fullName) nameRef.current?.focus();
    else if (found.email) emailRef.current?.focus();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setSubmitted(true);
    setExisting(null);
    setRefusal(null);
    setStale(null);
    const found = validate({ fullName, email });
    if (found.fullName || found.email) {
      showErrors(found);
      return;
    }
    if (outside) {
      emailRef.current?.focus();
      return;
    }
    const form = formRef.current;
    setPending(true);
    const state = await leadNow();
    await send(
      {
        fullName: fullName.trim(),
        email: trimmedEmail,
        ...(linked ? { personId: linked.id } : {}),
        expectedLeadId: state.lead?.userId ?? null,
      },
      form,
    );
  };

  /**
   * Запрос «Создать и назначить» и его ответ. «Повторить» в тосте шлёт тот же запрос; ответ
   * попадает в окно, только если оно показывает тот же протокол и то же заполнение шага (form),
   * иначе — тостом (W3, W6).
   */
  const send = async (body: CreateLeadBody, form: number): Promise<void> => {
    const same = () => here() && formRef.current === form;
    if (same()) setPending(true);
    try {
      const result = await api('minutes.lead.create', { params: { id }, body });
      refreshLead(queryClient, id, true);
      if (same()) onClose();
      // У создания «Отменить» нет: учётная запись и письмо остаются (spec, «Уточнения»).
      toast.ok(createdText(result.lead.name, number, body.email));
    } catch (error) {
      const retry = () => void send(body, form);
      if (!same()) {
        refuseAway(error, retry);
        return;
      }
      if (isApiError(error, 'REC-007') && (error.fields.fullName || error.fields.email)) {
        // Причина — под полем; S2: почта не та, что у человека справочника, — под «Почта».
        showErrors({ fullName: error.fields.fullName, email: error.fields.email });
      } else if (isApiError(error, 'REC-007') && error.fields.personId) {
        // Человека справочника нет или он не действует: окно остаётся, причина — над полями.
        setRefusal({ ...errorView(error), message: error.fields.personId, nextStep: PERSON_GONE });
      } else if (isApiError(error, 'REC-004') || isApiError(error, 'ACC-007')) {
        showErrors({ email: domains.length > 0 ? domainOnlyText(domains) : error.nextStep });
      } else if (isApiError(error, 'REC-003')) {
        const who = existingOf(error.nextStep);
        const view = errorView(error);
        showErrors({ email: who ? existingText(who.name) : error.message });
        if (who && error.body.existingUserId) {
          setExisting({ id: error.body.existingUserId, ...who, view });
        } else {
          setRefusal(view);
        }
      } else if (isApiError(error, 'MIN-025')) {
        staleRefused(error);
      } else if (isApiError(error, 'MIN-026')) {
        setRefusal(errorView(error));
      } else if (gone(error)) {
        refreshLead(queryClient, id);
        onClose();
        showError(error);
      } else {
        showError(error, retry);
      }
    } finally {
      // Окно закрыли — pending сброшен при закрытии; заполнение сменилось — это уже не его ответ.
      if (formRef.current === form) setPending(false);
    }
  };

  // Протокол ушёл в корзину или из режима ведения, пока окно открыто: отказ поиска или чтения
  // ведущего — тост с причиной, окно закрывается, как у назначения (W5). Одна ошибка — один тост.
  const goneError =
    lookup.error !== null && gone(lookup.error)
      ? lookup.error
      : lead.isError && !lead.isFetching && gone(lead.error)
        ? lead.error
        : null;
  const goneShown = useRef<unknown>(null);
  useEffect(() => {
    if (!open || goneError === null || goneShown.current === goneError) return;
    goneShown.current = goneError;
    refreshLead(queryClient, id);
    onClose();
    showError(goneError);
  }, [open, goneError, queryClient, id, onClose, showError]);

  // --- Разметка ---

  const staleCallout = stale ? (
    <Callout tone="warn" role="alert" code={stale.code} operation={stale.operation}>
      {sentence(stale)}
    </Callout>
  ) : null;

  const found = lookup.users.length + lookup.people.length;
  const typedEmail = isEmail(lookup.typed);
  const status = !lookup.ready
    ? TYPE_MORE
    : lookup.failure
      ? ''
      : searching
        ? SEARCHING
        : lookup.nobody
          ? nobodyText(lookup.typed)
          : found > 0 && !lookup.loading
            ? foundText(found)
            : '';
  // Число найденных — только для программы чтения: строки и так на виду.
  const statusShown = !lookup.ready || searching || lookup.nobody;

  const searchBody = (
    <div className="kp-ald" onKeyDown={(event) => moveFocus(event, resultsRef.current)}>
      {staleCallout}
      <p className="kp-ald-now">{current ? currentLeadText(current.name) : NO_LEAD}</p>
      {lead.isError && !lead.data ? (
        <ListLoadError
          what={LEAD_STATE_FAILED}
          view={errorView(lead.error)}
          retrying={lead.isFetching}
          onRetry={() => void lead.refetch()}
        />
      ) : null}
      <SearchField
        ref={searchRef}
        label={SEARCH_LABEL}
        placeholder={SEARCH_PLACEHOLDER}
        maxLength={LOOKUP_QUERY_MAX}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <div ref={resultsRef} className="kp-ald-results">
        {lookup.users.length > 0 ? (
          <Section heading={USERS_HEADING}>
            {lookup.users.map((user) => (
              <Option
                key={user.id}
                icon={UserRound}
                name={user.name}
                sub={user.email}
                note={user.external ? EXTERNAL_USER_REASON : undefined}
                // Пометка — по строке «Сейчас ведёт»: после MIN-025 она уже о новом ведущем (W1).
                badge={user.id === current?.userId ? <Badge>{LEADS_NOW}</Badge> : null}
                disabled={user.external}
                onChoose={() => void choose({ id: user.id, name: user.name })}
              />
            ))}
          </Section>
        ) : null}
        {lookup.people.length > 0 ? (
          <Section heading={PEOPLE_HEADING}>
            {lookup.people.map((item) => (
              <Option
                key={item.personId}
                icon={Contact}
                name={item.name}
                sub={[item.email, item.organization].filter(Boolean).join(' · ')}
                badge={
                  item.standing === 'corporate' ? null : (
                    <Badge tone="warn">{OUTSIDE_DOMAINS}</Badge>
                  )
                }
                disabled={!canCreateUser}
                describedBy={canCreateUser ? undefined : adminsId}
                onChoose={() =>
                  startCreate({
                    fullName: item.name,
                    email: item.email,
                    person: { id: item.personId, name: item.name, email: item.email },
                  })
                }
              />
            ))}
          </Section>
        ) : null}
        <p role="status" className={statusShown ? 'kp-ald-hint' : 'kp-sr-only'}>
          {status}
        </p>
        {lookup.ready ? (
          <ul className="kp-ald-list kp-ald-new" aria-label="Новый пользователь">
            <Option
              icon={UserPlus}
              name={createUserText(lookup.typed, typedEmail)}
              disabled={!canCreateUser}
              describedBy={canCreateUser ? undefined : adminsId}
              onChoose={() =>
                startCreate(
                  typedEmail
                    ? { fullName: '', email: lookup.typed, person: null }
                    : { fullName: lookup.typed, email: '', person: null },
                )
              }
            />
          </ul>
        ) : null}
        {lookup.ready && !canCreateUser ? (
          <div id={adminsId}>
            <Callout tone="info">
              {adminsOnlyText(me.admins.map((admin) => admin.fullName))}
            </Callout>
          </div>
        ) : null}
      </div>
      {lookup.failure ? (
        <ListLoadError
          what={LOOKUP_FAILED}
          view={lookup.failure}
          retrying={lookup.retrying}
          onRetry={lookup.retry}
        />
      ) : null}
    </div>
  );

  const emailLine = `${emailId}-line`;
  const createBody = (
    <form id={formId} className="t-col kp-form" noValidate onSubmit={(event) => void submit(event)}>
      {staleCallout}
      {refusal ? (
        <Callout tone="warn" role="alert" code={refusal.code} operation={refusal.operation}>
          {sentence(refusal)}
        </Callout>
      ) : null}
      <TextField
        ref={nameRef}
        label="ФИО"
        placeholder="например, Новиков Николай Николаевич"
        autoComplete="off"
        required
        value={fullName}
        error={errors.fullName}
        onChange={(event) => {
          setFullName(event.target.value);
          recheck({ fullName: event.target.value }, 'fullName');
        }}
      />
      <TextField
        ref={emailRef}
        id={emailId}
        label="Почта"
        type="email"
        inputMode="email"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        placeholder={`например, novikov@${domains[0] ?? 'example.ru'}`}
        required
        value={email}
        error={errors.email ?? (outside ? domainOnlyText(domains) : undefined)}
        onChange={(event) => {
          setEmail(event.target.value);
          setExisting(null);
          recheck({ email: event.target.value }, 'email');
        }}
      />
      {existing ? (
        <Callout
          tone="warn"
          code={existing.view.code}
          operation={existing.view.operation}
          action={
            existing.archived ? undefined : (
              <Button
                size="sm"
                onClick={() => void choose({ id: existing.id, name: existing.name })}
              >
                {assignExistingLabel(existing.name)}
              </Button>
            )
          }
        >
          {existing.archived ? ARCHIVED_TEXT : EXISTING_HINT}
        </Callout>
      ) : null}
      {standing === 'external' ? (
        <Callout
          tone="info"
          role="status"
          action={
            onOpenAccess ? (
              <Button
                size="sm"
                onClick={() => {
                  onClose();
                  // «Доступ» — после того, как это окно закрылось и вернуло фокус.
                  window.setTimeout(onOpenAccess);
                }}
              >
                {OPEN_ACCESS}
              </Button>
            ) : undefined
          }
        >
          {EXTERNAL_ACCESS_HINT}
        </Callout>
      ) : null}
      <Callout tone="info">{roleText(number, title)}</Callout>
      <p className="kp-ald-link">
        <Icon icon={Link} className="t-ico" />
        <span>{linked ? linkPersonText(linked.name) : LINK_BY_EMAIL}</span>
      </p>
      {current ? <Callout tone="warn">{leadNowText(current.name, after)}</Callout> : null}
    </form>
  );

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(next) => {
          // Пока идёт «Создать и назначить», ✕, Esc и «Отмена» ждут ответа (W6).
          if (!next && !pending) onClose();
        }}
        title={step === 'search' ? assignLeadTitle(number) : createTitle(number)}
        description={step === 'search' ? LEAD_MEANING : CREATE_SUBTITLE}
        className="kp-ald-modal"
        returnFocusRef={returnFocusRef}
        onEscapeKeyDown={(event) => {
          if (step === 'search') return;
          event.preventDefault();
          if (!pending) back();
        }}
        footer={
          step === 'search' ? (
            <ModalFooter />
          ) : (
            <ModalFooter
              secondary={
                <Button icon={ArrowLeft} disabled={pending} onClick={back}>
                  {BACK}
                </Button>
              }
              primary={
                <Button
                  type="submit"
                  form={formId}
                  variant="primary"
                  loading={pending}
                  disabled={outside}
                  aria-describedby={outside ? emailLine : undefined}
                >
                  {CREATE_AND_ASSIGN}
                </Button>
              }
            />
          )
        }
      >
        {step === 'search' ? searchBody : createBody}
      </Modal>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        tone="warn"
        icon={UserRoundCog}
        title={changeLeadTitle(number)}
        description={
          confirm ? changeLeadText(confirm.current.name, confirm.after, confirm.next.name) : null
        }
        confirmLabel={CHANGE_LEAD_CONFIRM}
        onConfirm={() => (confirm ? assign(confirm.next, confirm.current.userId) : undefined)}
      />
    </>
  );
}
